import { Accordion } from '@/components/_ui/Accordion'
import { Skeleton } from '@/components/_ui/Skeleton'
import useParseLinkUrlWithLocale from '@/lib/hooks/routing/useParseLinkUrlWithLocale'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'

import { Drawer, DrawerContent } from '@/components/_ui/Drawer'
import type { CosmicHeaderItemType } from '@/lib/cms/cosmic/types/CosmicHeaderType'
import { useState } from 'react'
import { ScrollArea } from '@/components/_ui/ScrollArea'
import { useNfrNavigationModal } from '@/lib/hooks/routing/useNfrNavigationModal'
import { MobileCountrySelector } from './MobileCountrySelector'
import { MobileDrawerFooter } from './MobileDrawerFooter'
import { MobileDrawerPromotions } from './MobileDrawerPromotions'
import { MobileMenuSubMenu } from './MobileMenuSubMenu'

export const MobileDrawer = () => {
  const {
    setApp,
    app: { isMobileDrawerOpen, marketExceptions },
    cms: { header },
    user: { isSignedIn },
  } = useGlobalStore()
  const { parseLinkUrlWithLocale } = useParseLinkUrlWithLocale()
  const { displayNfrNavigationModal } = useNfrNavigationModal()

  const [isCountrySelectorOpen, setIsCountrySelectorOpen] = useState(false)

  const navigationItem = ({ item }: { item: CosmicHeaderItemType }) => {
    const { withContent, href, content, title } = item.headerItem
    if (!withContent) {
      return (
        <a
          onClick={() => setApp({ isMobileDrawerOpen: false })}
          className="text-primary no-underline hover:underline"
          key={title}
          href={parseLinkUrlWithLocale({ url: href })}
        >
          {title}
        </a>
      )
    }

    return (
      <MobileMenuSubMenu
        rightSection={content?.rightSection}
        leftSection={content?.leftSection}
      />
    )
  }

  const renderNavigationItems = () => {
    if (header === 'error') {
      return (
        <div className="mt-10 flex flex-col items-center justify-center space-y-3">
          <Image
            className="px-3"
            alt="Unicity"
            src="/unicity.svg"
            width={160}
            height={25}
          />
          <span className="text-sm text-gray-500">
            We encountered an error loading our menu, we are working to fix it
          </span>
        </div>
      )
    }

    if (header === 'loading') {
      return (
        <div className="flex flex-col space-y-2 p-5">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      )
    }

    return (
      <div>
        <Accordion
          className="flex flex-col space-y-5"
          type="single"
          collapsible
        >
          {header?.items
            ?.filter((item) => item.headerItem?.withContent)
            .map((item) => {
              if (!item.headerItem) return null
              const { title, href } = item.headerItem
              return (
                <div key={`${title}-${href}`}>{navigationItem({ item })}</div>
              )
            })}
          {marketExceptions?.availableNfrMarkets &&
            isSignedIn &&
            marketExceptions.availableNfrMarkets.map((nfrMarket) => (
              <div key={nfrMarket.alpha2}>
                <button
                  type="button"
                  className="cursor-pointer font-medium text-primary hover:underline"
                  style={{
                    background: 'none',
                    border: 'none',
                    paddingLeft: 0,
                    paddingRight: 0,
                    textAlign: 'left',
                  }}
                  onClick={() => {
                    setApp({ isMobileDrawerOpen: false })
                    displayNfrNavigationModal(nfrMarket)
                  }}
                >
                  Shop {nfrMarket.label} Products
                </button>
              </div>
            ))}
        </Accordion>
      </div>
    )
  }

  return (
    <>
      <MobileCountrySelector
        isCountrySelectorOpen={isCountrySelectorOpen}
        setIsCountrySelectorOpen={setIsCountrySelectorOpen}
      />
      <Drawer
        direction="left"
        onOpenChange={(isOpen) => {
          setApp({ isMobileDrawerOpen: isOpen })
        }}
        open={isMobileDrawerOpen}
      >
        <DrawerContent className="flex size-full flex-col justify-between overflow-hidden">
          <ScrollArea className="relative h-full w-full">
            <div className="h-full p-5">
              <div className="mt-10">{renderNavigationItems()}</div>
              <div className="my-10 w-full">
                <MobileDrawerPromotions />
              </div>
            </div>
          </ScrollArea>
          <MobileDrawerFooter
            setIsCountrySelectorOpen={setIsCountrySelectorOpen}
          />
        </DrawerContent>
      </Drawer>
    </>
  )
}
